import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

const riskColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  moderate: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

const statusColors: Record<string, string> = {
  active: 'bg-teal-100 text-teal-700',
  closed: 'bg-gray-100 text-gray-600',
  archived: 'bg-gray-100 text-gray-500',
  referred: 'bg-blue-100 text-blue-700',
};

export default function ChildProfilesPage() {

  const [children, setChildren] = useState<any[]>([]);
  const { user } = useAuth();

  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const getRisk = (child: any) => {
    let score = 75;

    switch (child.incidentType) {
      case 'Sexual Abuse':
      case 'Child Trafficking':
      case 'Online Sexual Exploitation':
        score = 95;
        break;

      case 'Physical Abuse':
        score = 90;
        break;

      case 'Emotional Abuse':
        score = 80;
        break;

      case 'Neglect':
      case 'Child Labor':
      case 'Abandonment':
        score = 75;
        break;

      default:
        score = 70;
    }

    if (child.priorIncidents === 'yes') score += 5;
    if (child.priorityLevel === 'urgent') score += 3;
    if (child.priorityLevel === 'emergency') score += 5;

    if (score > 100) score = 100;

    let level = 'low';
    if (score >= 90) level = 'critical';
    else if (score >= 80) level = 'high';
    else if (score >= 75) level = 'moderate';

    return { riskScore: score, riskLevel: level };
  };

  useEffect(() => {
    if (!user?.uid) {
      console.log('No logged-in social worker UID');
      return;
    }

    const fetchChildren = async () => {
      try {
        const response = await fetch(
          `https://guardiancloudcare.com/backend/api/child_details.php?uid=${encodeURIComponent(
            user.uid
          )}`
        );

        const data = await response.json();

        console.log('CHILD PROFILES RESPONSE:', data);

        if (!response.ok || data.success === false) {
          throw new Error(
            data.error || 'Failed to fetch child profiles.'
          );
        }

        const childrenList = Array.isArray(data.data)
          ? data.data
          : [];

        const withRisk = childrenList.map((child: any) => {
          const risk = getRisk(child);

          return {
            ...child,
            riskScore: risk.riskScore,
            riskLevel: risk.riskLevel,

            // Make sure frontend always has an image field
            profile:
              child.profile ||
              child.profileImg ||
              null,
          };
        });

        setChildren(withRisk);

      } catch (error) {
        console.error(
          'Child profiles fetch error:',
          error
        );

        setChildren([]);
      }
    };

    fetchChildren();

  }, [user?.uid]);

  const filtered = children.filter((c) => {
    const name = `${c.firstName} ${c.lastName}`.toLowerCase();
    const matchSearch = name.includes(search.toLowerCase()) || c.caseNumber.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchRisk = filterRisk === 'all' || c.riskLevel === filterRisk;
    return matchSearch && matchStatus && matchRisk;
  });

  return (
    <AppLayout title="Child Profiles" breadcrumb={['Home', 'Child Profiles']}>
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 flex-1 min-w-48">
          <i className="ri-search-line text-gray-400 text-sm" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or case number..."
            className="bg-transparent text-sm outline-none w-full text-gray-700 placeholder-gray-400"
          />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none text-gray-700 cursor-pointer">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="referred">Referred</option>
          <option value="closed">Closed</option>
          <option value="archived">Archived</option>
        </select>
        <select value={filterRisk} onChange={(e) => setFilterRisk(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none text-gray-700 cursor-pointer">
          <option value="all">All Risk Levels</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="moderate">Moderate</option>
          <option value="low">Low</option>
        </select>
        <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1">
          <button onClick={() => setViewMode('grid')} className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${viewMode === 'grid' ? 'bg-teal-500 text-white' : 'text-gray-400 hover:bg-gray-100'}`}>
            <i className="ri-grid-line text-sm" />
          </button>
          <button onClick={() => setViewMode('list')} className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${viewMode === 'list' ? 'bg-teal-500 text-white' : 'text-gray-400 hover:bg-gray-100'}`}>
            <i className="ri-list-check text-sm" />
          </button>
        </div>
        <span className="text-sm text-gray-500 whitespace-nowrap">{filtered.length} records</span>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((child) => (
            <div
              key={child.caseNumber}
              onClick={() => navigate(`/child-profiles/${child.caseNumber}`)}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-teal-200 transition-all cursor-pointer group"
            >
              <div className="relative h-32 bg-gradient-to-br from-teal-50 to-gray-50 flex items-center justify-center">
                
                <img
                  src={child.profile}
                  alt={child.firstName}
                  className="w-20 h-20 rounded-full object-cover object-top border-4 border-white"
                />
                <span className={`absolute top-3 right-3 text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${riskColors[child.riskLevel]}`}>
                  {child.riskLevel}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 text-sm mb-0.5">{child.firstName} {child.lastName}</h3>
                <p className="text-xs text-gray-500 mb-2">{child.age} yrs • {child.gender} • {child.incidentType}</p>
                <p className="text-xs font-mono text-gray-400 mb-3">{child.caseNumber}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${statusColors[child.status]}`}>
                    {child.status}
                  </span>
                  <div className="flex items-center gap-1">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center">
                      <span className="text-teal-600 text-xs font-bold">{child.riskScore}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-5 py-3 text-left font-medium">Child</th>
                <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Case Number</th>
                <th className="px-5 py-3 text-left font-medium hidden lg:table-cell">Incident</th>
                <th className="px-5 py-3 text-left font-medium">Risk</th>
                <th className="px-5 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Score</th>
                <th className="px-5 py-3 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((child) => (
                
                <tr key={child.caseNumber} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={child.profile}
                        alt={child.firstName}
                        className="w-9 h-9 rounded-full object-cover object-top flex-shrink-0"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{child.firstName} {child.lastName}</p>
                        <p className="text-xs text-gray-400">{child.age} yrs • {child.gender}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell">
                    <span className="text-xs font-mono text-gray-500">{child.caseNumber}</span>
                  </td>
                  <td className="px-5 py-3 hidden lg:table-cell">
                    <span className="text-xs text-gray-600">{child.incidentType}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full border font-medium capitalize ${riskColors[child.riskLevel]}`}>
                      {child.riskLevel}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${statusColors[child.status]}`}>
                      {child.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell">
                    <span className="text-sm font-bold text-gray-700">{child.riskScore}</span>
                  </td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => navigate(`/child-profiles/${child.caseNumber}`)}
                      className="text-teal-500 hover:text-teal-700 text-xs font-medium cursor-pointer whitespace-nowrap"
                    >
                      View Profile
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppLayout>
  );
}
